import React from 'react';
import styled from 'styled-components';
import SlideLoading from './SlideLoading';
import { useWelcomeTourData } from '../useWelcomeTourData';
import { HeroHeadline, HeroLabel, SlideContainer, HeroBanner, HeroBody, BannerDecoClip, EmptyState } from './slideStyles';
import { getSelectedAssessmentPeriod } from '@/common/selectors/auth';
import { useSelector } from 'react-redux';

const ContentContainer = styled.div`
  width: min(100%, 1140px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderPanel = styled.div`
  width: 100%;
  padding: 0.75rem 1.75rem 1rem;
  box-sizing: border-box;

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 0.5rem 0.65rem 0.8rem;
  }
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
  }
`;

const HeroIntroText = styled.div`
  min-width: 0;
`;

const OpportunityCard = styled.div<{ $bg: string; $border: string }>`
  display: flex;
  align-items: center;
  gap: 1.75rem;
  min-width: 200px;
  background: ${props => props.$bg};
  border: 1px solid ${props => props.$border};
  border-radius: 0.75rem;
  padding: 1rem 1rem 1.25rem;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  margin-top: 1.25rem;

  
  @media (max-width: 900px) {
    display: none;
  }
`;

const OpportunityCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OpportunityCardLabel = styled.div<{ $color: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color};
`;

const OpportunityCardValue = styled.div<{ $color: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.$color};
`;

const ThemeStatusIcon = styled.span<{ $color: string }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${props => props.$color};
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;
  flex-shrink: 0;
  margin-left: 0.3rem;
`;

const PanelsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: stretch;
  width: 100%;
`;

const FeedbackPanel = styled.div<{ $bg: string; $border: string }>`
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  border-top: 1px solid rgba(148, 163, 184, 0.18);

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 1rem 0.75rem;
  }
`;

const PanelLabel = styled.div<{ $color: string }>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem;
  align-items: center;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(13px, 2vh, 16px);
  padding-bottom: 0.5rem;
  font-weight: 700;
  color: ${props => props.$color};
  letter-spacing: 0.05em;
  line-height: 1.3;
`;

const BulletList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const BulletItem = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-left: .75rem;
`;

const BulletIcon = styled.span<{ $color: string }>`
  color: ${props => props.$color};
  font-size: clamp(11px, 1.5vh, 13px);
  margin-top: 4px;
`;

const BulletText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(14px, 2vh, 16px);
  font-weight: 400;
  color: #374151;
  line-height: 1.55;
`;

const ExpandedSlideContainer = styled(SlideContainer)`
  height: 100%;
  flex: 1;
  min-height: 0;
`;

const PanelTitle = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(16px, 2vh, 20px);
  font-weight: 700;
  color: #C97918;
`;


function getSentimentScore(item: any): number {
    const score = item.sentiment_score ?? item.sentimentScore ?? item.score ?? 0;
    return typeof score === 'number' ? score : Number(score) || 0;
}

function formatSentimentRationale(item: any): string {
    const rationale = item.rationale || item.description || item.text || '';
    return [rationale].filter(Boolean).join(' ');
}

const Slide4GrowthOpportunities: React.FC = () => {
    const { analytics, isLoading, biggestOpportunity } = useWelcomeTourData();

    const sentimentRationales = (analytics?.sentiment_scores as any)?.team_pillar_sentiments as
        | Array<Record<string, any>>
        | undefined;
    const sortedSentiments = sentimentRationales
        ? [...sentimentRationales].sort((a, b) => getSentimentScore(b) - getSentimentScore(a))
        : [];
    const cappedSentiments = sortedSentiments.slice(0, 6);
    const strongestCount = Math.min(3, Math.ceil(cappedSentiments.length / 2));
    const weakestCount = Math.min(3, cappedSentiments.length - strongestCount);
    const weakestSentiments = weakestCount > 0 ? cappedSentiments.slice(-weakestCount) : [];
    const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);

    return (
        <ExpandedSlideContainer>
            <HeroBanner>
                <BannerDecoClip />
                <ContentContainer>
                    <HeaderPanel>
                        <HeaderGrid>
                            <HeroIntroText>
                                <HeroLabel>Growth opportunities - {selectedAssessmentPeriod?.name || 'Current Assessment Period'}</HeroLabel>
                                <HeroHeadline>Where your team can improve</HeroHeadline>
                                <HeroBody>
                                    These items highlight areas where feedback indicates the biggest opportunity for positive change.
                                </HeroBody>
                            </HeroIntroText>
                            {biggestOpportunity ? (
                                <OpportunityCard $bg="#FFFBEB" $border="#FDE68A">
                                    <OpportunityCardText>
                                        <OpportunityCardLabel $color="#F59E0B">Greatest Opportunity</OpportunityCardLabel>
                                        <OpportunityCardValue $color="#92400E">{biggestOpportunity.pillar_name}</OpportunityCardValue>
                                    </OpportunityCardText>
                                </OpportunityCard>
                            ) : null}
                        </HeaderGrid>
                    </HeaderPanel>

                    {isLoading ? (
                        <SlideLoading message="Loading growth opportunities..." />
                    ) : weakestSentiments.length > 0 ? (
                        <PanelsRow>
                            <FeedbackPanel $bg="#FFFCF6" $border="#F5DFC0">
                                <PanelLabel $color="#C97918">
                                    <ThemeStatusIcon $color="#C97918">•</ThemeStatusIcon>
                                    <PanelTitle>Growth Opportunities</PanelTitle>
                                </PanelLabel>
                                <BulletList>
                                    {weakestSentiments.map((entry, i) => (
                                        <BulletItem key={i}>
                                            <BulletIcon $color="#D97706">•</BulletIcon>
                                            <BulletText>{formatSentimentRationale(entry)}</BulletText>
                                        </BulletItem>
                                    ))}
                                </BulletList>
                            </FeedbackPanel>
                        </PanelsRow>
                    ) : (
                        <EmptyState>No growth opportunities are available yet for this slide.</EmptyState>
                    )}
                </ContentContainer>
            </HeroBanner>
        </ExpandedSlideContainer>
    );
};

export default Slide4GrowthOpportunities;
